import { CHEST_SHUT_VERB, DOOR_VERB, EffectLimit, LOCKED_LINE, NO_HANDLE, TALK_REFUSALS, TALK_VERB, TRAVEL_VERB, type MovementState } from "odyssey-shared";
import type { GameInput } from "../ui/input/router.ts";
import { say } from "../words.ts";
import type { ChestStore } from "./chest/store.ts";
import type { GameConnection, Interaction } from "./connection.ts";
import { pageKeys } from "./controls/page.ts";
import type { GroundPickUp } from "./ground/pick-up.ts";
import type { HarvestControl } from "./harvest/control.ts";
import { thingLine } from "./hud/words.ts";
import { newInteractContext, resolveInteractInto, type InteractContext } from "./interact-context.ts";
import { NO_TARGET, resolveTarget, thingReaches, type InteractTarget, type TargetLookup } from "./interact-target.ts";
import type { MessageLine } from "./message-line.ts";
import type { FrozenGate } from "./moderation/frozen-gate.ts";
import type { ReadingFeed } from "./reading/store.ts";
import { gameReport } from "./report.ts";
import type { TargetSource } from "./world-ui/target.ts";

/**
 * What the interaction needs from the scene, each read as it is asked: the target lookup once the world has loaded,
 * the connection while there is one, the predicted local character while a session has one, the `EffectLimit`
 * bits the server last confirmed for it, 0 for none, and whether the dialogue window is open (ADR-136 decision 5.6),
 * never on a page without one.
 */
export interface InteractWorld {
  readonly lookup: () => TargetLookup | null;
  readonly connection: () => GameConnection | null;
  readonly predicted: () => MovementState | undefined;
  readonly limits: () => number;
  readonly talking?: () => boolean;
}

/**
 * What E needs of a door entered with E: whether its prompt shows, and the handle the player has targeted, -1 for none.
 */
export interface DoorSource {
  readonly shown: boolean;
  readonly selected: number;
}

/**
 * E and what answers it (ADR-051, ADR-053, ADR-058): the thing E acts on, which the interaction prompt names from
 * the same lookup, the `interact` sent for it, the reply shown on the message line or in the reading panel
 * (P1.9-T37), and whether the thing the panel shows still reaches the character. The chest store is told which thing
 * each `interact` was sent for (P2.8-T16), so the `loot` that answers an opened chest opens the loot window and no
 * other does.
 *
 * While the character's effects forbid acting, a stun, the map server refuses its interact and answers nothing
 * (ADR-079's amendment of 2026-10-07, point 5), so the page sends none: the prompt withholds its key (`target`'s
 * `withheld`), drawn greyed, and E says nothing, since the prompt has said it. The server's refusal is the rule;
 * this spares the round trip. An open reading panel takes E before it reaches here, so a stun keeps none open.
 *
 * A thing the map server has answered `thing.locked` for is drawn in the design's `refused` state, "It is locked."
 * under its prompt, for as long as it offers the verb it was refused and until a press at it is answered otherwise
 * (ADR-058's amendment of 2026-10-10, point 10); the key stays offered and E still sends, since the map server decides
 * and a key picked up since may open it. Which things are locked never reaches the page before that answer.
 */
export class InteractControl {
  /**
   * The thing E acts on, as world-attached UI reads it (`world-ui/target.ts`): the interaction prompt names it from
   * the same lookup E resolves, so the two never disagree; the scene names no surface.
   */
  public readonly target: TargetSource = {
    current: () => this.currentTarget(),
    withheld: () => this.withheld(),
    refusal: (handle, verb) => this.refusal(handle, verb),
    scanCount: () => this.world.lookup()?.scans ?? 0,
  };

  /**
   * Whether a thing still reaches the predicted local character, for the reading panel and the loot window, made
   * once so asking it each frame allocates nothing.
   *
   * @param handle The thing's handle.
   * @returns True while the thing is in view and in reach by the rule E uses.
   */
  public readonly readingReaches = (handle: number): boolean => {
    const lookup = this.world.lookup();
    const connection = this.world.connection();
    const predicted = this.world.predicted();
    return lookup !== null && connection !== null && predicted !== undefined && thingReaches(lookup, handle, predicted, connection.view.entities);
  };

  /**
   * @param world What the interaction reads from the scene.
   * @param input The input router, whose owner says whether E is the game's.
   * @param reading The reading panel's store.
   * @param messageLine The message line.
   * @param chest The chest store, on a page with the loot window.
   * @param gate The page's refusal of an act while a Game Master has frozen its player; without one E is never
   *   refused for a freeze.
   * @param ground The page's pick-up of ground items, which E asks when no thing is its target (P2.8-T65); without
   *   one E picks nothing up.
   * @param harvest The page's harvesting (ADR-142 decision 3), whose prop in reach is the target when no thing is
   *   (a thing comes first); without one the grass offers nothing.
   */
  public constructor(
    private readonly world: InteractWorld,
    private readonly input: GameInput,
    private readonly reading: ReadingFeed,
    private readonly messageLine: MessageLine,
    private readonly chest?: Pick<ChestStore, "expect">,
    private readonly gate?: Pick<FrozenGate, "refuses">,
    private readonly ground?: Pick<GroundPickUp, "nearest" | "inReach">,
    private readonly harvest?: Pick<HarvestControl, "target" | "start">,
  ) {}

  /**
   * The prompt of a door entered with E and the handle the player has targeted, set once by the scene; none until then.
   */
  private door: DoorSource | null = null;

  /**
   * The choice `context` writes into.
   */
  private readonly choice = newInteractContext();

  /**
   * Gives E the door prompt (ADR-122 decision 2.8). While it shows, E asks the door unless the player has explicitly targeted
   * a thing in reach, which comes first; the door comes before harvesting and before a thing nobody targeted.
   *
   * @param door The prompt and the player's target.
   */
  public useDoor(door: DoorSource): void {
    this.door = door;
  }

  /**
   * The thing last answered `thing.locked`, -1 for none, and the verb it was refused.
   */
  private lockedHandle = -1;
  private lockedVerb = "";

  /** The line under the prompt for the refused thing, worded when its refusal arrived so a frame words nothing. */
  private lockedWords = "";

  /**
   * The thing E acts on for the predicted local character, from the lookup E and the interaction prompt share
   * (`interact-target.ts`): the first thing in view that offers a verb in its state (`verbFor` over the version's
   * contract, ADR-058) and is in range, by its activation cells where its state carries one (ADR-053) and the facing
   * rule otherwise, the same test the map server applies, so the key answers where the reply will. While a text field
   * or a hold owns the keyboard, E types or reaches nothing, and while the reading panel is open E closes it (P1.9-T37),
   * so nothing is the target and the prompt stands down; so too while the dialogue window is open, whose conversation
   * the server holds until it closes. The same holds while the topmost layer names the key
   * Interact is bound to, as the emote wheel names E to turn its page (ADR-114 decision 2.2).
   *
   * When no thing is in reach and a sickle is in hand, the target is the harvestable prop in reach (`harvest/look.ts`).
   *
   * @returns The target, or nothing before the world, the predictor and a playable connection exist, while the game
   *   does not own the keyboard, while the reading panel or the dialogue window is open, or while the topmost layer
   *   names Interact's key.
   */
  public currentTarget(): InteractTarget {
    const lookup = this.world.lookup();
    const connection = this.world.connection();
    const predicted = this.world.predicted();
    if (lookup === null || connection === null || predicted === undefined || !connection.ready || this.input.owner !== "game" || this.reading.open || this.world.talking?.() === true || this.input.takes(pageKeys.code("interact"))) {
      return NO_TARGET;
    }
    const thing = resolveTarget(lookup, predicted, connection.view.entities);
    return thing.found || thing.waiting || this.harvest === undefined ? thing : this.harvest.target(predicted.x, predicted.y);
  }

  /**
   * Whether the key is withheld: the character's effects forbid acting, as the map server reads it for an interact
   * (`EffectLimit.Act`, the bit an act is refused on).
   *
   * @returns True while an `interact` would be dropped as stunned.
   */
  public withheld(): boolean {
    return (this.world.limits() & EffectLimit.Act) !== 0;
  }

  /**
   * Why a thing the prompt names refused the verb it offers now: "It is locked." when it is the thing last answered
   * `thing.locked`, for that verb. A refusal held for another verb, the thing having changed state since, is let go.
   * Read every frame the prompt shows, so it calls nothing.
   *
   * @param handle The thing's handle.
   * @param verb The verb it offers now.
   * @returns The line, or "".
   */
  public refusal(handle: number, verb: string): string {
    if (handle !== this.lockedHandle) {
      return "";
    }
    if (verb !== this.lockedVerb) {
      this.lockedHandle = -1;
      return "";
    }
    return this.lockedWords;
  }

  /**
   * What E would do now, the one choice the key, the world prompt and the touch Interact button share
   * (`interact-context.ts`), written into one object the control keeps so a frame allocates nothing; read it before asking
   * again.
   *
   * @returns The choice.
   */
  public context(): InteractContext {
    resolveInteractInto(this.choice, this.currentTarget(), this.door?.shown === true, this.door?.selected ?? -1, this.ground?.inReach() === true, this.world.talking?.() === true);
    return this.choice;
  }

  /**
   * Aims the verb of the thing the prompt names (`currentTarget`) at it; with nothing in reach, says so at once,
   * since a dropped verb gets no reply (ADR-051). Beside a thing that is finishing its move and takes no key until it
   * has (ADR-058's amendment of 2026-10-07), it says nothing and sends nothing: there is something here, and its verb
   * comes back with the prompt. While a Game Master has frozen the player it sends nothing, expects no chest and
   * says the gate's line (ADR-056's amendment of 2026-10-06, decision 42). With a thing in reach while the key is
   * withheld (`withheld`), it sends nothing and says nothing: the greyed prompt is the answer. With no thing in reach
   * it picks up the nearest ground item in reach (`ground/pick-up.ts`, P2.8-T65), so a thing the prompt names always
   * comes first, and says there is nothing here only when no ground item is in reach either.
   *
   * Standing on a door entered with E (`useDoor`), E asks the door (`interact` with `NO_HANDLE` and `DOOR_VERB`), unless the
   * player has explicitly targeted a thing in reach, which comes first; the door beats harvesting, a thing nobody targeted and
   * a thing still finishing its move. The door's E is not withheld for a stun, as the map server takes it.
   */
  public interact(): void {
    const connection = this.world.connection();
    if (connection === null || this.world.predicted() === undefined || this.world.lookup() === null || !connection.ready) {
      return;
    }
    if (this.gate?.refuses() === true) {
      return;
    }
    const context = this.context();
    switch (context.kind) {
      case "verb": {
        if (this.withheld()) {
          return;
        }
        const target = this.currentTarget();
        this.chest?.expect(target.handle);
        connection.sendInteract(target.handle, context.verb);
        return;
      }
      case "harvest":
        if (!this.withheld()) {
          this.harvest?.start();
        }
        return;
      case "door":
        connection.sendInteract(NO_HANDLE, DOOR_VERB);
        return;
      case "pickUp":
        this.ground?.nearest();
        return;
      case "waiting":
        return;
      case "none":
        break;
    }
    this.messageLine.say(say("hud.line.nothingHere"));
  }

  /**
   * A left press on the world as a click on a shrine or a waypoint (ADR-137 decision 6): when the thing E would act
   * on offers `travel` and the point is inside its drawn box, it asks for travel as E does, under the same refusals
   * (no connection, a frozen player, a withheld key) and with the same reach, since the target is the one in reach. A
   * press on a place out of reach, or anywhere else, is left to the caller.
   *
   * @param x The press's world x in pixels.
   * @param y The press's world y in pixels.
   * @returns True when the press was taken as a click on the place and the request sent.
   */
  public clickTravel(x: number, y: number): boolean {
    const connection = this.world.connection();
    if (connection === null || !connection.ready || this.gate?.refuses() === true || this.withheld()) {
      return false;
    }
    const target = this.currentTarget();
    if (!target.found || target.verb !== TRAVEL_VERB || x < target.left || x > target.right || y < target.top || y > target.bottom) {
      return false;
    }
    connection.sendInteract(target.handle, target.verb);
    return true;
  }

  /**
   * The outcome of an `interact`: a refusal says why on the message line, a key the map server sent in place of a
   * sentence worded by the page (`thingLine`), or that nothing happens, and a success with something to say
   * opens the reading panel with it (P1.9-T37), replacing any reading it shows; a success with nothing to say shows
   * nothing. A `thing.locked` refusal marks the thing as refused for its verb, and any other answer for it clears
   * that; while the prompt names that thing the refusal is drawn under the prompt (`refusal`) and the message line says
   * nothing, and otherwise the message line says it, as any refusal. A refused `talk` carries a code the page words
   * (`talk.refused.*`, ADR-136 decision 5.2), and a granted one opens nothing here: the `dialogue` that follows opens
   * the dialogue window. The answer to a `shut` is shown nowhere: the page sends that verb as the loot
   * window closes, no key asks for it, and a map server that does not keep who holds a chest refuses it.
   *
   * @param interaction The outcome.
   */
  public receive(interaction: Interaction): void {
    gameReport.interaction = interaction;
    if (interaction.verb === CHEST_SHUT_VERB) {
      return;
    }
    if (interaction.ok || interaction.text !== LOCKED_LINE) {
      if (interaction.handle === this.lockedHandle) {
        this.lockedHandle = -1;
      }
    } else {
      this.lockedHandle = interaction.handle;
      this.lockedVerb = interaction.verb;
      this.lockedWords = thingLine(LOCKED_LINE) ?? "";
      const target = this.currentTarget();
      if (target.found && target.handle === interaction.handle) {
        return;
      }
    }
    if (!interaction.ok) {
      this.messageLine.say(interaction.text === "" ? say("hud.line.nothingHappens") : (refusalLine(interaction) ?? thingLine(interaction.text) ?? interaction.text));
      return;
    }
    if (interaction.verb === TALK_VERB) {
      return;
    }
    this.reading.receive(interaction);
  }
}

/**
 * The words for a refused `talk`'s code (ADR-136 decision 5.2): the author's "They eye your blade and wait." for
 * `combat`, and the author's choice of 2026-10-28 for `busy`.
 *
 * @param interaction The refused reply.
 * @returns The sentence, or null for a reply that is not a refused `talk` carrying a known code.
 */
function refusalLine(interaction: Interaction): string | null {
  if (interaction.verb !== TALK_VERB || !(TALK_REFUSALS as readonly string[]).includes(interaction.text)) {
    return null;
  }
  return interaction.text === "combat" ? say("talk.refused.combat") : say("talk.refused.busy");
}
